import {
  type CalendarEntryDto,
  DASHBOARD_SIZES,
  DASHBOARD_WIDGET_IDS,
  DASHBOARD_WIDGETS,
  type DashboardLayout,
  type DashboardPrefsDto,
  type DashboardSize,
  type DashboardWidgetId,
  datePl,
  DEFAULT_DASHBOARD,
  formatMoney,
  isModulePermission,
  type MeDto,
  processTaskLink,
  type MyWorkDto,
  type PlanningBoardDto,
  type PlanningQueueDto,
  type ProcessTaskDto,
  type TaskDto,
  todayInWarsaw,
} from '@bqcrm/shared';
import { ActionIcon, Anchor, Button, Card, Grid, Group, Modal, SegmentedControl, Stack, Text, ThemeIcon, Title, Tooltip, UnstyledButton } from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconArrowRight,
  IconBriefcase,
  IconCalendarEvent,
  IconCalendarStats,
  IconCircleCheck,
  IconCircleX,
  IconDeviceMobile,
  IconCash,
  IconClockExclamation,
  IconFileInvoice,
  IconGripVertical,
  IconLayoutDashboard,
  IconListCheck,
  IconPlus,
  IconTool,
  IconTruck,
  IconX,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../api';
import { can, canModule } from '../auth';
import { TaskStatusBadge } from '../components/Badges';
import { ask } from '../confirm';
import { notifyError, notifyOk } from '../notify';
import { EmptyState, SectionCard, StatCard } from '../components/ui';
import { useTrades, useTaskTypeLabel } from '../data';
import { plural } from '../format';
import { CaseRows } from './CasesPage';
import { FreeSlotsCard } from './dashboard/FreeSlotsCard';
import { ActionNeededCard, OffersAwaitingCard, ReceivablesCard, UpcomingWorkCard, useRoleData } from './dashboard/RolePanels';

interface SystemStatus {
  api: string;
  database: string;
  worker: { alive: boolean; lastHeartbeatSecondsAgo: number | null };
}

const longToday = () => {
  const s = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', weekday: 'long', day: 'numeric', month: 'long' }).format(new Date());
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const greeting = () => {
  const h = Number(new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', hour: 'numeric', hour12: false }).format(new Date()));
  return h >= 5 && h < 18 ? 'Dzień dobry' : 'Dobry wieczór';
};

/** Element pulpitu dostępny dla osoby (uprawnienia do jego danych). */
const available = (me: MeDto, id: DashboardWidgetId) => {
  const def = DASHBOARD_WIDGETS[id] as { need?: { permission: string; scope?: 'W' }[]; needAny?: { permission: string; scope?: 'W' }[] };
  return (def.need ?? []).every((n) => can(me, n.permission, n.scope)) && (!def.needAny || def.needAny.some((n) => can(me, n.permission, n.scope)));
};
/** Szerokość w siatce 12 kolumn: telefon — mały ½, reszta cała; tablet — ⅓ / ½; komputer — ¼ / ½ / całość. */
const SPAN: Record<DashboardSize, { base: number; sm: number; md: number; lg: number }> = {
  s: { base: 6, sm: 6, md: 4, lg: 3 },
  m: { base: 12, sm: 12, md: 6, lg: 6 },
  l: { base: 12, sm: 12, md: 12, lg: 12 },
};

/** Ramka elementu w trybie „Dostosuj pulpit”: przeciąganie, rozmiar, przesuwanie strzałkami (telefon, klawiatura), usunięcie. */
function EditFrame(p: { id: DashboardWidgetId; size: DashboardSize; first: boolean; last: boolean; onSize: (s: DashboardSize) => void; onMove: (d: -1 | 1) => void; onRemove: () => void; children: ReactNode }) {
  const def = DASHBOARD_WIDGETS[p.id];
  return (
    <Card padding={6} h="100%" style={{ border: '2px dashed var(--mantine-color-gray-4)', background: 'var(--app-muted, transparent)' }}>
      <Group gap={4} wrap="nowrap" mb={6} justify="space-between">
        <Group gap={4} wrap="nowrap" style={{ minWidth: 0, cursor: 'grab' }}>
          <IconGripVertical size={16} color="var(--mantine-color-dimmed)" style={{ flexShrink: 0 }} />
          <Text size="xs" fw={600} truncate>
            {def.title}
          </Text>
        </Group>
        <Tooltip label="Usuń z pulpitu">
          <ActionIcon variant="subtle" color="red" size="sm" onClick={p.onRemove} aria-label="Usuń z pulpitu">
            <IconX size={14} />
          </ActionIcon>
        </Tooltip>
      </Group>
      <Group gap={4} wrap="nowrap" mb={6} justify="space-between">
        {def.sizes.length > 1 ? (
          <SegmentedControl size="xs" value={p.size} onChange={(v) => p.onSize(v as DashboardSize)} data={def.sizes.map((x) => ({ value: x, label: DASHBOARD_SIZES[x].toLowerCase() }))} aria-label="Rozmiar" />
        ) : (
          <span />
        )}
        <Group gap={2} wrap="nowrap">
          <Tooltip label="Wcześniej">
            <ActionIcon variant="subtle" color="gray" size="sm" disabled={p.first} onClick={() => p.onMove(-1)} aria-label="Przesuń wcześniej">
              <IconArrowLeft size={14} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Dalej">
            <ActionIcon variant="subtle" color="gray" size="sm" disabled={p.last} onClick={() => p.onMove(1)} aria-label="Przesuń dalej">
              <IconArrowRight size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      {/* podgląd zawartości bez klikania w linki */}
      <div style={{ pointerEvents: 'none', opacity: 0.85 }}>{p.children}</div>
    </Card>
  );
}

/** Zadanie w terenie (dziś): godzina, klient, rodzaj, ekipa / osoby, stan. */
function FieldTaskRow({ t, color, people }: { t: TaskDto; color: string; people: string }) {
  const typeLabel = useTaskTypeLabel();
  const navigate = useNavigate();
  return (
    <UnstyledButton onClick={() => navigate(`/planowanie?zadanie=${t.id}`)} className="row-link" px="sm" py={8} style={{ borderRadius: 8, display: 'block', width: '100%' }}>
      <Group wrap="nowrap" gap="sm">
        <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 4, background: color }} />
        <Text size="sm" fw={600} w={96} c="dimmed" className="tabular">
          {t.timeFrom ? `${t.timeFrom}–${t.timeTo}` : 'cały dzień'}
        </Text>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={600} truncate>
            {t.clientName ?? t.title}
          </Text>
          <Text size="xs" c="dimmed" truncate>
            {typeLabel(t)} · {t.siteAddress ?? ''} {people ? `· ${people}` : ''}
          </Text>
        </div>
        <TaskStatusBadge status={t.status} />
      </Group>
    </UnstyledButton>
  );
}

/**
 * Pulpit: co dziś wymaga uwagi — liczby, praca w terenie, moje sprawy i zadania, stan systemu.
 * Układ ustawia każdy sam („Dostosuj pulpit”): które elementy, w jakiej kolejności i jak duże. Zapis na serwerze.
 */
export function DashboardPage({ me }: { me: MeDto }) {
  const qc = useQueryClient();
  const prefs = useQuery({ queryKey: ['dashboard-prefs'], queryFn: () => api<DashboardPrefsDto>('/me/dashboard') });
  const [draft, setDraft] = useState<DashboardLayout | null>(null);
  const [adding, setAdding] = useState(false);
  const [dragId, setDragId] = useState<DashboardWidgetId | null>(null);
  const [overId, setOverId] = useState<DashboardWidgetId | null>(null);
  const saved = prefs.data?.layout ?? DEFAULT_DASHBOARD;
  const editing = draft !== null;
  const layout = (draft ?? saved).items.filter((i) => available(me, i.id));
  const saveLayout = useMutation({
    mutationFn: (l: DashboardLayout | null) => (l ? api<DashboardPrefsDto>('/me/dashboard', { method: 'PUT', body: l }) : api<DashboardPrefsDto>('/me/dashboard', { method: 'DELETE' })),
    onSuccess: (d) => {
      qc.setQueryData(['dashboard-prefs'], d);
      setDraft(null);
      notifyOk('Zapisano układ pulpitu');
    },
    onError: notifyError,
  });
  const change = (fn: (items: DashboardLayout['items']) => DashboardLayout['items']) => setDraft((d) => ({ items: fn((d ?? saved).items.filter((i) => available(me, i.id))) }));
  const moveTo = (id: DashboardWidgetId, target: DashboardWidgetId) =>
    change((items) => {
      if (id === target) return items;
      const from = items.findIndex((i) => i.id === id);
      const it = items[from]!;
      const rest = items.filter((i) => i.id !== id);
      const to = rest.findIndex((i) => i.id === target);
      // przeciągnięcie w przód — przed element docelowy; w tył — za niego
      rest.splice(from < items.findIndex((i) => i.id === target) ? to + 1 : to, 0, it);
      return rest;
    });
  const step = (id: DashboardWidgetId, d: -1 | 1) =>
    change((items) => {
      const i = items.findIndex((x) => x.id === id);
      const j = i + d;
      if (j < 0 || j >= items.length) return items;
      const copy = [...items];
      [copy[i], copy[j]] = [copy[j]!, copy[i]!];
      return copy;
    });
  const missing = DASHBOARD_WIDGET_IDS.filter((id) => available(me, id) && !layout.some((i) => i.id === id));
  const typeLabel = useTaskTypeLabel();
  const today = todayInWarsaw();
  const trades = useTrades();
  const canCal = can(me, 'kalendarz.podglad');
  const canPlan = can(me, 'planowanie.edycja');
  const status = useQuery({ queryKey: ['system-status'], queryFn: () => api<SystemStatus>('/system/status'), refetchInterval: 30_000 });
  const service = useQuery({ queryKey: ['service-counters'], queryFn: () => api<{ ticketsToArrange: number; urgentToArrange?: number; overduePlans: number; protocolErrors: number; claimsOpen?: number; claimsOverdue?: number } | null>('/service/counters'), enabled: can(me, 'serwis.podglad', 'W') && canModule(me, 'serwis') });
  const work = useQuery({ queryKey: ['cases', 'my-work'], queryFn: () => api<MyWorkDto>('/cases/my-work'), enabled: can(me, 'sprawy.podglad', 'W') });
  // zadania procesowe (docs/10) — zastąpiły kroki „Przebiegu”
  const myTasks = useQuery({ queryKey: ['process-inbox', 'moje'], queryFn: () => api<ProcessTaskDto[]>('/process/inbox?widok=moje'), enabled: can(me, 'sprawy.podglad'), refetchInterval: 60_000 });
  const board = useQuery({ queryKey: ['planning-board', today, today], queryFn: () => api<PlanningBoardDto>(`/planning/board?from=${today}&to=${today}`), enabled: canCal });
  const queue = useQuery({ queryKey: ['planning-queue'], queryFn: () => api<PlanningQueueDto>('/planning/queue'), enabled: canPlan });
  const absences = useQuery({ queryKey: ['absences-pending'], queryFn: () => api<CalendarEntryDto[]>('/calendar/absences/pending') });

  const role = useRoleData(me);
  const todayTasks = (board.data?.tasks ?? []).filter((t) => t.status !== 'anulowane' && t.assignments.some((a) => a.day === today)).sort((a, b) => (a.timeFrom ?? '00').localeCompare(b.timeFrom ?? '00'));
  const blocked = (board.data?.tasks ?? []).filter((t) => t.status === 'wstrzymane');
  const name = (id: string) => board.data?.people.find((p) => p.id === id)?.fullName ?? '';
  const team = (id: string | null) => board.data?.teams.find((t) => t.id === id)?.name ?? null;
  const color = (t: TaskDto) => trades.data?.find((x) => x.code === t.scopes[0]?.tradeCode)?.color ?? '#0ca678';
  const overdueTasks = (myTasks.data ?? []).filter((t) => t.overdue);
  const toPlan = queue.data ? queue.data.tasks.length + queue.data.scopes.length + queue.data.continue.length : null;

  const attention: { key: string; icon: ReactNode; color: string; text: ReactNode; to: string }[] = [
    ...blocked.map((t) => ({
      key: `b${t.id}`,
      icon: <IconAlertTriangle size={16} />,
      color: 'red',
      text: (
        <>
          Wstrzymane: <b>{t.clientName ?? t.title}</b> — {typeLabel(t).toLowerCase()}
        </>
      ),
      to: `/planowanie?zadanie=${t.id}`,
    })),
    ...(service.data?.overduePlans
      ? [
          {
            key: 'ov',
            icon: <IconClockExclamation size={16} />,
            color: 'red',
            text: (
              <>
                Zaległe przeglądy: <b>{service.data.overduePlans}</b> — ryzyko utraty gwarancji
              </>
            ),
            to: '/serwis?widok=przeglady',
          },
        ]
      : []),
    ...(service.data?.claimsOverdue
      ? [
          {
            key: 'rk',
            icon: <IconClockExclamation size={16} />,
            color: 'orange',
            text: (
              <>
                Reklamacje bez decyzji producenta po terminie: <b>{service.data.claimsOverdue}</b>
              </>
            ),
            to: '/serwis?widok=reklamacje',
          },
        ]
      : []),
    ...(service.data?.protocolErrors
      ? [
          {
            key: 'pe',
            icon: <IconAlertTriangle size={16} />,
            color: 'orange',
            text: (
              <>
                Błędy PDF / wysyłki protokołów: <b>{service.data.protocolErrors}</b>
              </>
            ),
            to: '/serwis',
          },
        ]
      : []),
    ...(absences.data?.length
      ? [
          {
            key: 'ab',
            icon: <IconCalendarEvent size={16} />,
            color: 'yellow',
            text: (
              <>
                Wnioski o nieobecność do zatwierdzenia: <b>{absences.data.length}</b>
              </>
            ),
            to: '/kalendarz',
          },
        ]
      : []),
    ...overdueTasks.slice(0, 5).map((t) => ({
      key: `t${t.id}`,
      icon: <IconListCheck size={16} />,
      color: 'orange',
      text: (
        <>
          Zadanie po terminie: <b>{t.title}</b> · {t.caseNumber}
        </>
      ),
      to: processTaskLink(t),
    })),
  ];

  // zawartość elementów pulpitu (null — brak danych do pokazania, np. serwis bez dostępu)
  const content: Record<DashboardWidgetId, ReactNode> = {
    'stat.oferty': role.offers.data ? (<StatCard
            label="Oferty czekające na klienta"
            value={role.offers.data.length}
            icon={<IconFileInvoice size={22} />}
            color="indigo"
            to="/sprawy"
            hint={role.offersSum ? `do ${formatMoney(role.offersSum)} brutto` : 'czekają na decyzję'}
          />) : null,
    'stat.po_terminie': role.receivables.data ? (<StatCard
            label="Po terminie"
            value={role.overdue.length ? formatMoney(role.overdueSum) : '0 zł'}
            icon={<IconCash size={22} />}
            color="red"
            to="/finanse"
            hint={role.overdue.length ? `${role.overdue.length} ${plural(role.overdue.length, 'należność', 'należności', 'należności')}` : 'brak zaległych należności'}
            tone={role.overdue.length ? 'alert' : undefined}
          />) : null,
    'stat.moje_sprawy': work.data ? (<StatCard
            label="Moje otwarte sprawy"
            value={work.data.casesTotal}
            icon={<IconBriefcase size={22} />}
            to="/moje-sprawy"
            hint={`${myTasks.data?.length ?? 0} ${plural(myTasks.data?.length ?? 0, 'zadanie', 'zadania', 'zadań')} do zrobienia`}
          />) : null,
    'stat.teren_dzis': (<StatCard
            label="Dziś w terenie"
            value={board.data ? todayTasks.length : '…'}
            icon={<IconTruck size={22} />}
            color="blue"
            to="/planowanie"
            hint={blocked.length ? `${blocked.length} ${plural(blocked.length, 'wstrzymane', 'wstrzymane', 'wstrzymanych')}` : 'zadań zaplanowanych na dziś'}
            tone={blocked.length ? 'alert' : undefined}
          />),
    'stat.do_zaplanowania': (<StatCard
            label="Do zaplanowania"
            value={toPlan ?? '…'}
            icon={<IconCalendarStats size={22} />}
            color="grape"
            to="/planowanie"
            hint={queue.data?.continue.length ? `w tym ${queue.data.continue.length} do kontynuacji` : 'zadania i montaże po umowie'}
          />),
    'stat.serwis': service.data ? (<StatCard
            label="Serwis do umówienia"
            value={service.data.ticketsToArrange}
            icon={<IconTool size={22} />}
            color="teal"
            to="/serwis"
            hint={[service.data.urgentToArrange ? `w tym pilne: ${service.data.urgentToArrange}` : '', service.data.overduePlans ? `zaległe przeglądy: ${service.data.overduePlans}` : ''].filter(Boolean).join(' · ') || 'zgłoszenia i przeglądy'}
            tone={service.data.overduePlans || service.data.urgentToArrange ? 'alert' : undefined}
          />) : null,
    'stat.reklamacje': service.data ? (<StatCard
            label="Reklamacje u producenta"
            value={service.data.claimsOpen ?? 0}
            icon={<IconTool size={22} />}
            color="orange"
            to="/serwis?widok=reklamacje"
            hint={service.data.claimsOverdue ? `po terminie decyzji: ${service.data.claimsOverdue}` : 'zgłoszone i zaakceptowane'}
            tone={service.data.claimsOverdue ? 'alert' : undefined}
          />) : null,
    'karta.do_dzialania': <ActionNeededCard d={role} />,
    'karta.oferty': <OffersAwaitingCard d={role} />,
    'karta.najblizsze_prace': <UpcomingWorkCard d={role} />,
    'karta.wolne_terminy': <FreeSlotsCard me={me} />,
    'karta.naleznosci': <ReceivablesCard d={role} />,
    'lista.teren_dzis': (<SectionCard
                title="Dziś w terenie"
                icon={<IconTruck size={16} />}
                actions={
                  <Anchor component={Link} to="/planowanie" size="sm">
                    Otwórz plan
                  </Anchor>
                }
                padding="sm"
              >
                {todayTasks.length ? (
                  <Stack gap={2}>
                    {todayTasks.map((t) => {
                      const ppl = [...new Set(t.assignments.filter((a) => a.day === today).map((a) => name(a.userId).split(' ').slice(-1)[0]))].join(', ');
                      const seg = t.segments.find((s) => s.dateFrom <= today && s.dateTo >= today);
                      return <FieldTaskRow key={t.id} t={seg ? { ...t, timeFrom: seg.timeFrom, timeTo: seg.timeTo } : t} color={color(t)} people={[team(seg?.teamId ?? t.teamId), ppl].filter(Boolean).join(': ')} />;
                    })}
                  </Stack>
                ) : (
                  <EmptyState compact icon={<IconTruck size={18} />} title="Na dziś nic nie zaplanowano" hint={canPlan ? 'Zadania do zaplanowania czekają w kolejce Planowania.' : undefined} />
                )}
              </SectionCard>),
    'lista.moje_sprawy': work.data ? (<SectionCard
                title={`Moje otwarte sprawy (${work.data.casesTotal})`}
                icon={<IconBriefcase size={16} />}
                actions={
                  <Anchor component={Link} to="/moje-sprawy" size="sm">
                    Etapy i blokady
                  </Anchor>
                }
                padding={0}
              >
                {work.data.cases.length ? (
                  <>
                    <CaseRows items={work.data.cases} showOwner={false} />
                    {work.data.casesTotal > work.data.cases.length && (
                      <Text size="xs" c="dimmed" px="md" py="xs">
                        Pokazano {work.data.cases.length} z {work.data.casesTotal} — wszystkie w „Moje sprawy”.
                      </Text>
                    )}
                  </>
                ) : (
                  <EmptyState compact icon={<IconBriefcase size={18} />} title="Nie jesteś opiekunem żadnej otwartej sprawy" hint="Tu pojawią się sprawy, w których jesteś opiekunem — od zapytania po realizację umowy." />
                )}
              </SectionCard>) : null,
    'lista.wymaga_uwagi': (<SectionCard title="Wymaga uwagi" icon={<IconAlertTriangle size={16} />} padding="sm">
              {attention.length ? (
                <Stack gap={2}>
                  {attention.map((a) => (
                    <UnstyledButton key={a.key} component={Link} to={a.to} className="row-link" px="sm" py={8} style={{ borderRadius: 8 }}>
                      <Group gap="sm" wrap="nowrap" align="flex-start">
                        <ThemeIcon size="sm" variant="light" color={a.color} mt={2}>
                          {a.icon}
                        </ThemeIcon>
                        <Text size="sm">{a.text}</Text>
                      </Group>
                    </UnstyledButton>
                  ))}
                </Stack>
              ) : (
                <EmptyState compact icon={<IconCircleCheck size={18} />} title="Wszystko w porządku" hint="Brak wstrzymanych zadań, zaległości i wniosków do decyzji." />
              )}
            </SectionCard>),
    'lista.moje_zadania': (<SectionCard
                title={`Moje zadania (${myTasks.data?.length ?? 0})`}
                icon={<IconListCheck size={16} />}
                padding="sm"
                actions={
                  <Anchor component={Link} to="/zadania" size="sm">
                    Wszystkie
                  </Anchor>
                }
              >
                {myTasks.data?.length ? (
                <Stack gap={2}>
                  {myTasks.data.slice(0, 8).map((t) => (
                    <UnstyledButton key={t.id} component={Link} to={processTaskLink(t)} className="row-link" px="sm" py={6} style={{ borderRadius: 8 }}>
                      <Text size="sm" fw={600}>
                        {t.title}
                      </Text>
                      <Text size="xs" c={t.overdue ? 'red' : 'dimmed'}>
                        {t.clientName} · {t.caseNumber} · {t.dueAt ? `do ${datePl(t.dueAt)}` : 'bez terminu'}
                      </Text>
                    </UnstyledButton>
                  ))}
                </Stack>
                ) : (
                  <EmptyState compact icon={<IconListCheck size={18} />} title="Nie masz otwartych zadań" />
                )}
              </SectionCard>),
    system: (<Card padding="md">
              <Group justify="space-between">
                <Text size="sm" fw={600}>
                  Stan systemu
                </Text>
                {status.data && (
                  <Group gap={10}>
                    {[
                      { ok: status.data.api === 'ok', label: 'Serwer aplikacji' },
                      { ok: status.data.database === 'ok', label: 'Baza danych' },
                      { ok: status.data.worker.alive, label: `Zadania w tle${status.data.worker.lastHeartbeatSecondsAgo !== null ? ` — sygnał ${status.data.worker.lastHeartbeatSecondsAgo} s temu` : ''}` },
                    ].map((x) => (
                      <Tooltip key={x.label} label={x.label}>
                        <ThemeIcon size="sm" radius="xl" variant="light" color={x.ok ? 'green' : 'red'}>
                          {x.ok ? <IconCircleCheck size={14} /> : <IconCircleX size={14} />}
                        </ThemeIcon>
                      </Tooltip>
                    ))}
                  </Group>
                )}
              </Group>
            </Card>),
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <div>
          <Text size="sm" c="dimmed">
            {longToday()}
          </Text>
          <Title order={1}>
            {greeting()}, {me.fullName.split(' ')[0]}
          </Title>
        </div>
        <Group gap="xs">
          {!editing && DASHBOARD_WIDGET_IDS.some((id) => available(me, id)) && (
            <Button variant="subtle" color="gray" leftSection={<IconLayoutDashboard size={16} />} onClick={() => setDraft({ items: layout })}>
              Dostosuj pulpit
            </Button>
          )}
          {canPlan && canModule(me, 'planowanie') && (
            <Button variant="default" leftSection={<IconCalendarStats size={16} />} component={Link} to="/planowanie">
              Planowanie
            </Button>
          )}
          {can(me, 'serwis.tworzenie', 'W') && canModule(me, 'serwis') && (
            <Button variant="default" leftSection={<IconPlus size={16} />} component={Link} to="/serwis">
              Zgłoszenie serwisowe
            </Button>
          )}
        </Group>
      </Group>

      {/* konto bez modułów menu (np. monter, rola bez modułów) — panel działa, praca jest w aplikacji terenowej */}
      {!Object.keys(me.permissions).some(isModulePermission) && (
        <Card padding="lg">
          <Group justify="space-between" wrap="wrap" gap="md">
            <Group gap="md" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
              <ThemeIcon size={44} radius="xl" variant="light">
                <IconDeviceMobile size={22} />
              </ThemeIcon>
              <div style={{ minWidth: 0 }}>
                <Text fw={700}>Twoja praca jest w aplikacji terenowej</Text>
                <Text size="sm" c="dimmed">
                  Zadania, protokoły, zgłoszenia i urlopy obsłużysz w aplikacji na telefonie albo tutaj, w przeglądarce. Jeśli potrzebujesz w panelu spraw, klientów czy planowania, poproś administratora o nadanie roli.
                </Text>
              </div>
            </Group>
            <Button component="a" href="/teren" leftSection={<IconDeviceMobile size={16} />}>
              Otwórz aplikację terenową
            </Button>
          </Group>
        </Card>
      )}

      {editing && (
        <Card padding="sm" withBorder style={{ borderColor: 'var(--mantine-color-eko-4, var(--mantine-color-teal-4))' }}>
          <Group justify="space-between" wrap="wrap" gap="xs">
            <Text size="sm">
              <b>Dostosuj pulpit:</b> przeciągnij element za uchwyt (albo przesuń strzałkami), wybierz rozmiar (mały / średni / szeroki), usuń zbędne, dodaj brakujące.
            </Text>
            <Group gap="xs">
              <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} disabled={!missing.length} onClick={() => setAdding(true)}>
                Dodaj element{missing.length ? ` (${missing.length})` : ''}
              </Button>
              <Button
                size="xs"
                variant="subtle"
                color="gray"
                onClick={async () => (await ask('Pulpit wróci do układu domyślnego (wszystkie elementy dostępne dla Ciebie, w standardowej kolejności).', { title: 'Przywrócić układ domyślny?', confirm: 'Przywróć' })) && saveLayout.mutate(null)}
              >
                Przywróć domyślny
              </Button>
              <Button size="xs" variant="default" onClick={() => setDraft(null)}>
                Anuluj
              </Button>
              <Button size="xs" loading={saveLayout.isPending} onClick={() => saveLayout.mutate({ items: layout })}>
                Zapisz układ
              </Button>
            </Group>
          </Group>
        </Card>
      )}

      {layout.length === 0 ? (
        <Card padding={0}>
          <EmptyState
            icon={<IconLayoutDashboard size={20} />}
            title="Pulpit jest pusty"
            hint={editing ? 'Dodaj elementy przyciskiem „Dodaj element”.' : 'Kliknij „Dostosuj pulpit” i dodaj elementy, które chcesz widzieć.'}
          />
        </Card>
      ) : (
        <Grid gap="md">
          {layout.map((it, n) => {
            const body = content[it.id];
            if (!editing && body === null) return null;
            const inner = body ?? (
              <Card padding="md">
                <Text size="sm" c="dimmed">
                  {DASHBOARD_WIDGETS[it.id].title} — brak danych do pokazania
                </Text>
              </Card>
            );
            return (
              <Grid.Col
                key={it.id}
                span={SPAN[it.size]}
                draggable={editing}
                onDragStart={(e) => {
                  if (!editing) return;
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', it.id);
                  setDragId(it.id);
                }}
                onDragOver={(e) => {
                  if (!editing || !dragId) return;
                  e.preventDefault();
                  if (overId !== it.id) setOverId(it.id);
                }}
                onDragLeave={() => overId === it.id && setOverId(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragId) moveTo(dragId, it.id);
                  setDragId(null);
                  setOverId(null);
                }}
                onDragEnd={() => {
                  setDragId(null);
                  setOverId(null);
                }}
                style={editing ? { opacity: dragId === it.id ? 0.4 : 1, outline: overId === it.id && dragId !== it.id ? '2px solid var(--mantine-color-blue-5)' : undefined, borderRadius: 12 } : undefined}
              >
                {editing ? (
                  <EditFrame
                    id={it.id}
                    size={it.size}
                    first={n === 0}
                    last={n === layout.length - 1}
                    onSize={(sz) => change((items) => items.map((x) => (x.id === it.id ? { ...x, size: sz } : x)))}
                    onMove={(d) => step(it.id, d)}
                    onRemove={() => change((items) => items.filter((x) => x.id !== it.id))}
                  >
                    {inner}
                  </EditFrame>
                ) : (
                  inner
                )}
              </Grid.Col>
            );
          })}
        </Grid>
      )}

      <Modal opened={adding} onClose={() => setAdding(false)} title="Dodaj element pulpitu">
        <Stack gap="xs">
          {missing.map((id) => (
            <Group key={id} justify="space-between" wrap="nowrap" gap="sm">
              <div style={{ minWidth: 0 }}>
                <Text size="sm" fw={600}>
                  {DASHBOARD_WIDGETS[id].title}
                </Text>
                <Text size="xs" c="dimmed">
                  {DASHBOARD_WIDGETS[id].description}
                </Text>
              </div>
              <Button
                size="xs"
                variant="light"
                onClick={() => {
                  change((items) => [...items, { id, size: DASHBOARD_WIDGETS[id].defaultSize }]);
                  if (missing.length <= 1) setAdding(false);
                }}
              >
                Dodaj
              </Button>
            </Group>
          ))}
          {!missing.length && <Text size="sm" c="dimmed">Wszystkie dostępne elementy są już na pulpicie.</Text>}
        </Stack>
      </Modal>
    </Stack>
  );
}
